import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { GroupBase, StylesConfig } from 'react-select';
import { User, Shield, GraduationCap, MapPin, Code, Lock, Unlock, CheckCircle, FileText, Eye, Download, Trash2, ExternalLink, FolderGit2, Image as ImageIcon, Upload } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@context/AuthContext';
import { STUDENT_DASHBOARD_KEY } from '@services/student/studentQueries';
import { studentService } from '@services/student/studentService';
import { authService } from '@services/auth/authService';
import applicationsService from '@services/applications/applicationsService';
import { useStudentTheme } from '@context/StudentThemeContext';
import { useRipple } from '@/hooks/useRipple';
import Button from '@components/Button/Button';
import Input from '@components/Form/Input/Input';
import Select from '@components/Form/Select/Select';
import type { SelectOption } from '@components/Form/Select/Select';
import Textarea from '@components/Form/Textarea/Textarea';
import Modal from '@components/common/Modal/Modal';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';

// react-select renders via inline styles, so it can't be re-themed with scoped CSS
// like the rest of the Student Portal — this is passed as the `styles` override
// prop only when the Student Portal's dark theme is active.
const darkSelectStyles: StylesConfig<SelectOption, false, GroupBase<SelectOption>> = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? '#4C8DFF' : '#2A3350',
        backgroundColor: state.isDisabled ? '#1A2236' : '#10182C',
        '&:hover': {
            borderColor: state.isFocused ? '#4C8DFF' : '#2A3350',
        },
    }),
    menu: (base) => ({
        ...base,
        backgroundColor: '#141B2E',
        border: '1px solid #2A3350',
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px',
        backgroundColor: '#141B2E',
    }),
    singleValue: (base) => ({
        ...base,
        color: '#E8EBF5',
    }),
    input: (base) => ({
        ...base,
        color: '#E8EBF5',
    }),
    placeholder: (base) => ({
        ...base,
        color: '#6B7691',
    }),
    option: (base, state) => ({
        ...base,
        color: state.isSelected ? '#ffffff' : '#E8EBF5',
        backgroundColor: state.isSelected ? '#4C8DFF' : state.isFocused ? '#1B2A4A' : 'transparent',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#6B7691',
        '&:hover': { color: '#E8EBF5' },
    }),
    clearIndicator: (base) => ({
        ...base,
        color: '#6B7691',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
};

interface ProfileFormValues {
    first_name: string;
    email: string;
    date_of_birth: string;
    parent_name: string;
    contact_number: string;
    country: string;
    state: string;
    city: string;
    school_name: string;
    school_type: "Public" | "Private" | "";
    grade: string;
    programming_experience: string;
    accomplishments: string;
    discovery_source: string;
    referral_code: string;
    project_title: string;
    project_description: string;
}

type ProfileSection = 'basic_details' | 'school_contact' | 'academic_profile';

// Fields required within each verifiable section — used to gate that section's Verify button
const SECTION_FIELDS: Record<ProfileSection, (keyof ProfileFormValues)[]> = {
    basic_details: ['first_name', 'date_of_birth', 'parent_name', 'contact_number'],
    school_contact: ['country', 'state', 'city'],
    academic_profile: ['school_name', 'school_type', 'grade', 'programming_experience'],
};

const StudentProfile: React.FC = () => {
    useTranslation('students');
    const { theme } = useStudentTheme();
    const selectStyles = theme === 'dark' ? darkSelectStyles : undefined;
    const ripple = useRipple();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [countries, setCountries] = useState<any[]>([]);
    const [countryOptions, setCountryOptions] = useState<{ value: string; label: string }[]>([]);
    const [stateOptions, setStateOptions] = useState<{ value: string; label: string }[]>([]);
    const [avatarPath, setAvatarPath] = useState<string | null>(null);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [presentationPath, setPresentationPath] = useState<string | null>(null);
    const [presentationName, setPresentationName] = useState<string | null>(null);
    const [isUploadingPresentation, setIsUploadingPresentation] = useState(false);
    const [screenshotPath, setScreenshotPath] = useState<string | null>(null);
    const [screenshotName, setScreenshotName] = useState<string | null>(null);
    const [isUploadingScreenshot, setIsUploadingScreenshot] = useState(false);
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [verifiedSections, setVerifiedSections] = useState<Record<ProfileSection, boolean>>({
        basic_details: false,
        school_contact: false,
        academic_profile: false,
    });
    const [verifyingSection, setVerifyingSection] = useState<ProfileSection | null>(null);
    const queryClient = useQueryClient();
    const { refreshProfile } = useAuth();

    const notifyProfileCompletionUpdated = (
        nextVerified = verifiedSections,
        nextAvatar = avatarPath,
        nextPresentation = presentationPath
    ) => {
        const count = 
            (nextVerified.basic_details ? 1 : 0) +
            (nextVerified.school_contact ? 1 : 0) +
            (nextVerified.academic_profile ? 1 : 0) +
            (nextAvatar ? 1 : 0) +
            (nextPresentation ? 1 : 0);
        const percentage = Math.round((count / 5) * 100);
        const isComplete = percentage === 100;

        window.dispatchEvent(new CustomEvent('student-profile-completion-updated', {
            detail: {
                profile_completion: {
                    percentage,
                    is_complete: isComplete,
                    sections: {
                        basic_details: nextVerified.basic_details,
                        school_contact: nextVerified.school_contact,
                        academic_profile: nextVerified.academic_profile,
                        profile_picture: !!nextAvatar,
                        project_upload: !!nextPresentation,
                    }
                }
            }
        }));
        queryClient.invalidateQueries({ queryKey: STUDENT_DASHBOARD_KEY });
    };

    const getFileUrl = (path: string | null) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        const backendBase = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5050/api/v1').replace('/api/v1', '');
        return `${backendBase}/${path}`;
    };
    const avatarUrl = avatarPath ? getFileUrl(avatarPath) : '';

    const countWords = (text: string) => {
        if (!text) return 0;
        const words = text.trim().split(/\s+/).filter(Boolean);
        return words.length;
    };

    // <input type="date"> needs a plain YYYY-MM-DD value, not the ISO timestamp the API returns.
    const toDateInputValue = (value: string | null | undefined) => (value ? value.slice(0, 10) : '');

    const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 2 * 1024 * 1024) {
            toast.error('File size must be less than 2MB');
            return;
        }

        try {
            setIsUploadingAvatar(true);
            const formData = new FormData();
            formData.append('file', file);

            const res = await studentService.uploadProfilePicture(formData);
            const newPath = res.data.data.profile_picture;
            setAvatarPath(newPath);
            await refreshProfile();
            notifyProfileCompletionUpdated(verifiedSections, newPath, presentationPath);
            toast.success('Profile picture updated successfully');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to upload profile picture'));
        } finally {
            setIsUploadingAvatar(false);
        }
    };

    const handleRemoveAvatar = async () => {
        try {
            setIsUploadingAvatar(true);
            await studentService.deleteProfilePicture();
            setAvatarPath(null);
            await refreshProfile();
            notifyProfileCompletionUpdated(verifiedSections, null, presentationPath);
            toast.success('Profile picture removed successfully');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to remove profile picture'));
        } finally {
            setIsUploadingAvatar(false);
        }
    };

    const { register, handleSubmit, control, reset, watch, formState: { errors } } = useForm<ProfileFormValues>({
        defaultValues: {
            first_name: '',
            email: '',
            date_of_birth: '',
            parent_name: '',
            contact_number: '',
            country: '',
            state: '',
            city: '',
            school_name: '',
            school_type: '',
            grade: '',
            programming_experience: '1',
            accomplishments: '',
            discovery_source: '',
            referral_code: '',
            project_title: '',
            project_description: '',
        }
    });

    const selectedCountry = watch('country');
    const watchedProfileValues = watch();

    const handlePresentationChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        if (!isPdf) {
            toast.error('Only PDF files are allowed for demo presentation');
            e.target.value = '';
            return;
        }

        if (file.size > 25 * 1024 * 1024) {
            toast.error('File size must be less than 25MB');
            e.target.value = '';
            return;
        }

        try {
            setIsUploadingPresentation(true);
            const formData = new FormData();
            formData.append('file', file);

            const res = await studentService.uploadProjectPresentation(formData);
            const data = res.data.data;
            setPresentationPath(data.demo_presentation_path);
            setPresentationName(data.demo_presentation_name);
            notifyProfileCompletionUpdated(verifiedSections, avatarPath, data.demo_presentation_path);
            toast.success('Demo presentation uploaded successfully');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to upload demo presentation'));
        } finally {
            setIsUploadingPresentation(false);
            e.target.value = '';
        }
    };

    const handleRemovePresentation = async () => {
        try {
            setIsUploadingPresentation(true);
            await studentService.deleteProjectPresentation();
            setPresentationPath(null);
            setPresentationName(null);
            notifyProfileCompletionUpdated(verifiedSections, avatarPath, null);
            toast.success('Demo presentation removed successfully');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to remove demo presentation'));
        } finally {
            setIsUploadingPresentation(false);
        }
    };

    const handleScreenshotChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const isImage = file.type.startsWith('image/') || /\.(png|jpe?g|webp)$/i.test(file.name);
        if (!isImage) {
            toast.error('Only image files (PNG, JPG, JPEG, WEBP) are allowed for the screenshot');
            e.target.value = '';
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            e.target.value = '';
            return;
        }

        try {
            setIsUploadingScreenshot(true);
            const formData = new FormData();
            formData.append('file', file);

            const res = await studentService.uploadProjectScreenshot(formData);
            const data = res.data.data;
            setScreenshotPath(data.project_screenshot_path);
            setScreenshotName(data.project_screenshot_name);
            toast.success('Project screenshot uploaded successfully');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to upload project screenshot'));
        } finally {
            setIsUploadingScreenshot(false);
            e.target.value = '';
        }
    };

    const handleRemoveScreenshot = async () => {
        try {
            setIsUploadingScreenshot(true);
            await studentService.deleteProjectScreenshot();
            setScreenshotPath(null);
            setScreenshotName(null);
            toast.success('Project screenshot removed successfully');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to remove project screenshot'));
        } finally {
            setIsUploadingScreenshot(false);
        }
    };

    // Load countries metadata on mount
    useEffect(() => {
        const loadMetadata = async () => {
            try {
                const res = await applicationsService.getCountriesStates();
                const list = res.data || [];
                setCountries(list);
                setCountryOptions(list.map((c: any) => ({
                    value: c.name,
                    label: `${c.flag} ${c.name} (${c.phoneCode})`,
                })));
            } catch (err) {
                console.error("Failed to load country/state list", err);
            }
        };
        loadMetadata();
    }, []);

    // Watch country changes to populate state choices
    useEffect(() => {
        if (!selectedCountry || countries.length === 0) {
            setStateOptions([]);
            return;
        }
        const matched = countries.find(c => c.name === selectedCountry);
        if (matched && matched.states) {
            setStateOptions(matched.states.map((s: string) => ({ value: s, label: s })));
        } else {
            setStateOptions([]);
        }
    }, [selectedCountry, countries]);

    // Load user profile details
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setIsLoading(true);
                const res = await studentService.getProfile();
                const profile = res.data.data;
                setAvatarPath(profile.profile_picture || null);
                setPresentationPath(profile.demo_presentation_path || null);
                setPresentationName(profile.demo_presentation_name || null);
                setScreenshotPath(profile.project_screenshot_path || null);
                setScreenshotName(profile.project_screenshot_name || null);
                const updatedVerified = {
                    basic_details: !!profile.is_basic_details_verified,
                    school_contact: !!profile.is_school_contact_verified,
                    academic_profile: !!profile.is_academic_profile_verified,
                };
                setVerifiedSections(updatedVerified);
                notifyProfileCompletionUpdated(
                    updatedVerified,
                    profile.profile_picture || null,
                    profile.demo_presentation_path || null
                );
                reset({
                    ...profile,
                    date_of_birth: toDateInputValue(profile.date_of_birth),
                    programming_experience: profile.programming_experience ? String(profile.programming_experience) : '1',
                    school_type: profile.school_type || '',
                    project_title: profile.project_title || '',
                    project_description: profile.project_description || '',
                });
            } catch (err) {
                toast.error(getApiErrorMessage(err, 'Failed to load profile details'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchProfile();
    }, [reset]);

    const onSubmit = async (data: ProfileFormValues) => {
        try {
            setIsSaving(true);
            const payload = {
                first_name: data.first_name,
                date_of_birth: data.date_of_birth || undefined,
                parent_name: data.parent_name,
                contact_number: data.contact_number,
                country: data.country,
                state: data.state,
                city: data.city,
                school_name: data.school_name,
                school_type: data.school_type,
                grade: data.grade,
                programming_experience: Number(data.programming_experience),
                accomplishments: data.accomplishments,
                discovery_source: data.discovery_source,
                project_title: data.project_title,
                project_description: data.project_description,
            };
            const res = await studentService.updateProfile(payload);
            toast.success(res.data.message || 'Profile updated successfully');
            
            // Re-fetch profile to refresh form with latest state
            const refreshRes = await studentService.getProfile();
            const updatedProfile = refreshRes.data.data;
            setAvatarPath(updatedProfile.profile_picture || null);
            setPresentationPath(updatedProfile.demo_presentation_path || null);
            setPresentationName(updatedProfile.demo_presentation_name || null);
            setScreenshotPath(updatedProfile.project_screenshot_path || null);
            setScreenshotName(updatedProfile.project_screenshot_name || null);
            const updatedVerified = {
                basic_details: !!updatedProfile.is_basic_details_verified,
                school_contact: !!updatedProfile.is_school_contact_verified,
                academic_profile: !!updatedProfile.is_academic_profile_verified,
            };
            setVerifiedSections(updatedVerified);
            notifyProfileCompletionUpdated(
                updatedVerified,
                updatedProfile.profile_picture || null,
                updatedProfile.demo_presentation_path || null
            );
            reset({
                ...updatedProfile,
                date_of_birth: toDateInputValue(updatedProfile.date_of_birth),
                programming_experience: updatedProfile.programming_experience ? String(updatedProfile.programming_experience) : '1',
                school_type: updatedProfile.school_type || '',
                project_title: updatedProfile.project_title || '',
                project_description: updatedProfile.project_description || '',
            });
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to update profile'));
        } finally {
            setIsSaving(false);
        }
    };

    const isSectionFilled = (section: ProfileSection) =>
        SECTION_FIELDS[section].every((field) => !!String(watchedProfileValues[field] ?? '').trim());

    const handleVerifySection = async (section: ProfileSection) => {
        try {
            setVerifyingSection(section);
            const res = await studentService.verifyProfileSection(section);
            const flags = res.data.data;
            const updatedVerified = {
                basic_details: !!flags.is_basic_details_verified,
                school_contact: !!flags.is_school_contact_verified,
                academic_profile: !!flags.is_academic_profile_verified,
            };
            setVerifiedSections(updatedVerified);
            notifyProfileCompletionUpdated(updatedVerified, avatarPath, presentationPath);
            toast.success('Section verified successfully');
        } catch (err) {
            toast.error(getApiErrorMessage(err, 'Failed to verify this section'));
        } finally {
            setVerifyingSection(null);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const gradeOptions = [8, 9, 10, 11, 12].map((g) => ({
        value: `Grade ${g}`,
        label: `Grade ${g}`,
    }));

    const discoverySourceOptions = [
        "Social Media",
        "Google Search",
        "Friend / Family / Alumni",
        "School / Teacher",
        "YouTube",
        "Other"
    ].map(o => ({ value: o, label: o }));

    // Completion is based on: 3 verified sections + profile picture + project demo presentation PDF, 20% each (5 total).
    const totalCompletionFields = 5;
    const completedFieldsCount =
        (verifiedSections.basic_details ? 1 : 0) +
        (verifiedSections.school_contact ? 1 : 0) +
        (verifiedSections.academic_profile ? 1 : 0) +
        (avatarPath ? 1 : 0) +
        (presentationPath ? 1 : 0);
    const completionPercentage = Math.round((completedFieldsCount / totalCompletionFields) * 100);
    const isProfileComplete = completionPercentage === 100;

    const renderVerifyControl = (section: ProfileSection) => {
        if (verifiedSections[section]) {
            return (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-full">
                    <CheckCircle size={14} />
                    Verified
                </span>
            );
        }
        const filled = isSectionFilled(section);
        return (
            <button
                type="button"
                onClick={() => handleVerifySection(section)}
                onPointerDown={ripple.onPointerDown}
                disabled={!filled || verifyingSection === section}
                title={!filled ? 'Fill in all required fields in this section first' : undefined}
                className="ripple-trigger inline-flex items-center gap-1.5 text-xs font-semibold text-primary border border-primary/30 px-3 py-1.5 rounded-full transition-all duration-300 hover:bg-blue-bg hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
                {verifyingSection === section ? 'Verifying...' : 'Verify'}
            </button>
        );
    };

    return (
        <div className="space-y-6 max-w-4xl mx-auto pb-12">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
                    <User className="text-primary" />
                    Edit Profile
                </h1>
            </div>

            {/* Profile Completion Card */}
            <div className="sticky top-[69px] z-20 bg-white rounded-2xl p-6 shadow-soft border border-border">
                <div className="flex items-center justify-between gap-4 mb-3">
                    <div>
                        <span className="text-sm font-semibold text-navy">Profile Completion</span>
                        <p className="text-xs text-slate-body mt-0.5">
                            {isProfileComplete
                                ? 'Your profile is all set!'
                                : `${completedFieldsCount} of ${totalCompletionFields} completed`}
                        </p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-2xl font-bold ${isProfileComplete ? 'text-emerald-600' : 'text-primary'}`}>
                        {isProfileComplete && <CheckCircle size={22} />}
                        {completionPercentage}%
                    </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                        className={`h-2.5 rounded-full transition-all duration-700 ${isProfileComplete ? 'bg-emerald-500' : 'bg-primary'}`}
                        style={{ width: `${completionPercentage}%` }}
                    />
                </div>

                {/* Skill Profile Unlock Status & Action */}
                <div className="mt-4 pt-3.5 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap text-xs">
                        {isProfileComplete ? (
                            <span className="inline-flex items-center gap-1.5 font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-lg">
                                <Unlock size={14} className="text-emerald-600" />
                                Skill Profile Unlocked (100%)
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg">
                                <Lock size={14} className="text-amber-600" />
                                Skill Profile Locked ({completionPercentage}%)
                            </span>
                        )}
                        <span className="text-slate-500 text-[11.5px]">
                            {isProfileComplete
                                ? 'Your verified credential & project showcase are ready to view and share.'
                                : 'Please complete your profile section to 100% to unlock your Skill Profile.'}
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={() => {
                            if (!isProfileComplete) {
                                toast.error('Please complete your profile section to 100% after that unlock', {
                                    id: 'skill-profile-gated-warning',
                                    duration: 4500,
                                    icon: '🔒',
                                });
                                return;
                            }
                            navigate('/student/skill-profile');
                        }}
                        className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all duration-200 cursor-pointer shrink-0 ${
                            isProfileComplete
                                ? 'bg-primary hover:bg-blue-600 text-white shadow-xs'
                                : 'bg-gray-100 hover:bg-amber-50 text-gray-500 hover:text-amber-800 border border-gray-200 hover:border-amber-300'
                        }`}
                        title={!isProfileComplete ? 'Please complete your profile section to 100% after that unlock' : 'View your official Skill Profile'}
                    >
                        {isProfileComplete ? <Unlock size={13} /> : <Lock size={13} />}
                        <span>View Skill Profile</span>
                        {isProfileComplete && <ExternalLink size={13} />}
                    </button>
                </div>
            </div>

            {/* Profile Avatar Card */}
            <div className="bg-white rounded-2xl p-6 shadow-soft border border-border flex flex-col md:flex-row items-center gap-6">
                <div className="relative group shrink-0">
                    <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-primary bg-gray-50 flex items-center justify-center shrink-0">
                        {avatarUrl ? (
                            <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                        ) : (
                            <User size={40} className="text-gray-400" />
                        )}
                    </div>
                    {isUploadingAvatar && (
                        <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        </div>
                    )}
                </div>
                <div className="space-y-2 text-center md:text-left flex-1">
                    <h3 className="text-lg font-bold text-navy">Profile Picture</h3>
                    <p className="text-xs text-slate-body">Upload a high-quality square picture. Max size 2MB.</p>
                    <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                        <label
                            onPointerDown={ripple.onPointerDown}
                            style={{ '--ripple-color': 'rgba(255,255,255,0.5)' } as React.CSSProperties}
                            className="ripple-trigger px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-full text-xs font-semibold shadow-soft cursor-pointer transition-all duration-300 hover:-translate-y-0.5 inline-block"
                        >
                            Choose File
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleAvatarChange}
                                disabled={isUploadingAvatar}
                            />
                        </label>
                        {avatarPath && (
                            <button
                                type="button"
                                onClick={handleRemoveAvatar}
                                onPointerDown={ripple.onPointerDown}
                                className="ripple-trigger px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-full text-xs font-semibold transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
                            >
                                Remove
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* 1. Basic Details Section */}
                <div className="bg-white rounded-2xl p-6 shadow-soft border border-border space-y-4">
                    <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
                        <h2 className="text-lg font-bold text-navy flex items-center gap-2">
                            <User className="text-primary" size={18} />
                            Basic Details
                        </h2>
                        {renderVerifyControl('basic_details')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                            label="Student's Full Name"
                            placeholder="Enter full name"
                            required
                            registration={register('first_name', { required: "Student's Full Name is required" })}
                            error={errors.first_name?.message}
                        />
                        <Input
                            label="Date of Birth"
                            type="date"
                            required
                            max={new Date().toISOString().slice(0, 10)}
                            registration={register('date_of_birth', { required: 'Date of Birth is required' })}
                            error={errors.date_of_birth?.message}
                        />
                        <Input
                            label="Parent's / Guardian's Full Name"
                            placeholder="Enter parent's full name"
                            required
                            registration={register('parent_name', { required: "Parent's / Guardian's Full Name is required" })}
                            error={errors.parent_name?.message}
                        />
                        <div className="relative">
                            <Input
                                label="Email Address (Locked)"
                                placeholder="Enter email"
                                disabled
                                registration={register('email')}
                                className="bg-gray-100 cursor-not-allowed opacity-75 pr-10"
                            />
                            <Lock size={16} className="absolute right-3 bottom-3 text-gray-400" />
                        </div>
                        <Input
                            label="Parent's Phone Number"
                            placeholder="Enter parent's phone number"
                            required
                            registration={register('contact_number', { required: "Parent's Phone Number is required" })}
                            error={errors.contact_number?.message}
                        />
                    </div>
                </div>

                {/* 2. Address & Location Section */}
                <div className="bg-white rounded-2xl p-6 shadow-soft border border-border space-y-4">
                    <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
                        <h2 className="text-lg font-bold text-navy flex items-center gap-2">
                            <MapPin className="text-primary" size={18} />
                            School & Contact Details
                        </h2>
                        {renderVerifyControl('school_contact')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Controller
                            name="country"
                            control={control}
                            rules={{ required: 'Country is required' }}
                            render={({ field }) => (
                                <Select
                                    label="Country"
                                    placeholder="Select Country"
                                    required
                                    options={countryOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.country?.message}
                                    styles={selectStyles}
                                />
                            )}
                        />
                        <Controller
                            name="state"
                            control={control}
                            rules={{ required: 'State / Region is required' }}
                            render={({ field }) => (
                                <Select
                                    label="State"
                                    placeholder="Select State"
                                    required
                                    options={stateOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.state?.message}
                                    styles={selectStyles}
                                    isDisabled={stateOptions.length === 0}
                                />
                            )}
                        />
                        <Input
                            label="City"
                            placeholder="Enter city"
                            required
                            registration={register('city', { required: 'City is required' })}
                            error={errors.city?.message}
                        />
                    </div>
                </div>

                {/* 3. Academic Profile Section */}
                <div className="bg-white rounded-2xl p-6 shadow-soft border border-border space-y-6">
                    <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
                        <h2 className="text-lg font-bold text-navy flex items-center gap-2">
                            <GraduationCap className="text-primary" size={18} />
                            Academic Profile
                        </h2>
                        {renderVerifyControl('academic_profile')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div className="md:col-span-2">
                            <Input
                                label="Name of School"
                                placeholder="Enter school name"
                                required
                                registration={register('school_name', { required: 'School name is required' })}
                                error={errors.school_name?.message}
                            />
                        </div>
                        {/* School Type Button Tabs Selection */}
                        <Controller
                            name="school_type"
                            control={control}
                            rules={{ required: 'School type is required' }}
                            render={({ field }) => (
                                <div className="flex flex-col gap-1 mb-2">
                                    <span className="text-[12px] font-medium text-[var(--secondary-color)] mb-1">
                                        School Type <span className="text-[var(--semantic-error-color)] ml-0.5">*</span>
                                    </span>
                                    <div className="inline-flex rounded-full bg-gray-100 p-1 w-fit border border-gray-200">
                                        {(["Public", "Private"] as const).map((tType) => (
                                            <button
                                                key={tType}
                                                type="button"
                                                onClick={() => field.onChange(tType)}
                                                onPointerDown={ripple.onPointerDown}
                                                className={`ripple-trigger px-4 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ${
                                                    field.value === tType
                                                        ? "bg-primary text-white shadow-soft"
                                                        : "text-slate-body hover:text-navy"
                                                }`}
                                            >
                                                {tType}
                                            </button>
                                        ))}
                                    </div>
                                    {errors.school_type && (
                                        <span className="text-[11px] text-[var(--semantic-error-color)] mt-1" role="alert">
                                            {errors.school_type.message}
                                        </span>
                                    )}
                                </div>
                            )}
                        />
                        <Controller
                            name="grade"
                            control={control}
                            rules={{ required: 'Grade is required' }}
                            render={({ field }) => (
                                <Select
                                    label="Current Grade"
                                    placeholder="Select Grade"
                                    required
                                    options={gradeOptions}
                                    value={field.value}
                                    onChange={field.onChange}
                                    error={errors.grade?.message}
                                    styles={selectStyles}
                                />
                            )}
                        />
                    </div>

                    {/* Programming Experience Button Cards Section */}
                    <Controller
                        name="programming_experience"
                        control={control}
                        rules={{ required: 'Experience level rating is required' }}
                        render={({ field }) => (
                            <div className="flex flex-col gap-1 w-full pt-2">
                                <span className="text-[12px] font-medium text-[var(--secondary-color)] mb-1">
                                    Please rate programming experience from 1 to 5 <span className="text-[var(--semantic-error-color)] ml-0.5">*</span>
                                </span>
                                <div className="grid grid-cols-5 gap-2 w-full mt-1 bg-blue-bg/40 p-4 rounded-2xl border border-border">
                                    {[
                                        { val: '1', lbl: 'New' },
                                        { val: '2', lbl: 'Basic' },
                                        { val: '3', lbl: 'A few projects' },
                                        { val: '4', lbl: 'Intermediate' },
                                        { val: '5', lbl: 'Strong' },
                                    ].map((exp) => (
                                        <button
                                            key={exp.val}
                                            type="button"
                                            onClick={() => field.onChange(exp.val)}
                                            onPointerDown={ripple.onPointerDown}
                                            className={`ripple-trigger flex flex-col items-center justify-center py-3 rounded-xl border transition-all duration-300 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 ${
                                                field.value === exp.val
                                                    ? "bg-primary border-primary text-white shadow-card transform scale-105"
                                                    : "bg-white border-border text-navy hover:bg-blue-bg"
                                            }`}
                                        >
                                            <span className="text-lg font-bold">{exp.val}</span>
                                            <span className={`text-[10px] mt-1 font-medium ${
                                                field.value === exp.val ? "text-blue-100" : "text-slate-body"
                                            }`}>
                                                {exp.lbl}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                {errors.programming_experience && (
                                    <span className="text-[11px] text-[var(--semantic-error-color)] mt-1" role="alert">
                                        {errors.programming_experience.message}
                                    </span>
                                )}
                                <p className="text-[11px] text-slate-body mt-1 italic">
                                    No programming experience is required to join the program, but this helps us place you in the right team.
                                </p>
                            </div>
                        )}
                    />
                </div>

                {/* 4. Additional Details Section */}
                <div className="bg-white rounded-2xl p-6 shadow-soft border border-border space-y-4">
                    <h2 className="text-lg font-bold text-navy flex items-center gap-2 border-b border-border pb-3">
                        <Code className="text-primary" size={18} />
                        Additional Details
                    </h2>
                    <div className="grid grid-cols-1 gap-4">
                        <Textarea
                            label="Briefly share activities, awards & accomplishments"
                            placeholder="e.g. Led a team project on social issues, received distinction in International Math Assessment..."
                            required
                            rows={3}
                            registration={register('accomplishments', { required: 'Accomplishments is required' })}
                            error={errors.accomplishments?.message}
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Controller
                                name="discovery_source"
                                control={control}
                                rules={{ required: 'Discovery source is required' }}
                                render={({ field }) => (
                                    <Select
                                        label="Discovery Source"
                                        placeholder="Select Source"
                                        required
                                        options={discoverySourceOptions}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={errors.discovery_source?.message}
                                        styles={selectStyles}
                                    />
                                )}
                            />
                            <div className="relative">
                                <Input
                                    label="Referral Code (Locked)"
                                    placeholder="Enter referral code"
                                    disabled
                                    registration={register('referral_code')}
                                    className="bg-gray-100 cursor-not-allowed opacity-75 pr-10"
                                />
                                <Lock size={16} className="absolute right-3 bottom-3 text-gray-400" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* 5. Project Details Section */}
                <div className="bg-white rounded-2xl p-6 shadow-soft border border-border space-y-4">
                    <div className="flex justify-between items-center border-b border-border pb-3">
                        <h2 className="text-lg font-bold text-navy flex items-center gap-2">
                            <FolderGit2 className="text-primary" size={18} />
                            Project Details
                        </h2>
                        <div className="flex items-center gap-2">
                            {presentationPath ? (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-full">
                                    <CheckCircle size={14} />
                                    PDF Uploaded
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full">
                                    <Lock size={12} />
                                    PDF Required (20%)
                                </span>
                            )}
                            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                Capstone Showcase
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {/* Project Title */}
                        <div>
                            <Input
                                label="Project Title"
                                placeholder="e.g. AI-Powered Healthcare Assistant"
                                registration={register('project_title')}
                                error={errors.project_title?.message}
                            />
                        </div>

                        {/* Project Description (250 words limit) */}
                        <div>
                            <div className="flex justify-between items-center mb-1">
                                <label className="text-xs font-semibold text-navy">
                                    Project Description
                                </label>
                                <span
                                    className={`text-[11px] font-bold ${
                                        countWords(watch('project_description') || '') > 250
                                            ? 'text-rose-600'
                                            : countWords(watch('project_description') || '') > 220
                                            ? 'text-amber-600'
                                            : 'text-slate-400'
                                    }`}
                                >
                                    {countWords(watch('project_description') || '')} / 250 words
                                </span>
                            </div>
                            <Textarea
                                placeholder="Describe your project, core problem, solution, and key impact (maximum 250 words)..."
                                rows={4}
                                registration={register('project_description', {
                                    validate: (val) =>
                                        countWords(val || '') <= 250 ||
                                        `Project description cannot exceed 250 words (${countWords(val || '')} words entered)`,
                                })}
                                error={errors.project_description?.message}
                            />
                            <p className="text-[11px] text-slate-body mt-1">
                                Words are calculated by spaces. Up to 250 words allowed.
                            </p>
                        </div>

                        {/* Demo Presentation (PDF Upload + Preview) */}
                        <div className="space-y-2 pt-2 border-t border-border">
                            <label className="text-xs font-semibold text-navy block">
                                Demo Presentation (PDF Format)
                            </label>

                            {presentationPath ? (
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-xl bg-rose-50 border border-rose-200 text-rose-600 flex items-center justify-center shrink-0">
                                            <FileText size={22} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-navy truncate max-w-[200px] sm:max-w-[300px]">
                                                    {presentationName || 'demo_presentation.pdf'}
                                                </span>
                                                <span className="text-[10px] uppercase font-extrabold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 shrink-0">
                                                    PDF
                                                </span>
                                            </div>
                                            <span className="text-[11px] text-emerald-600 font-medium flex items-center gap-1 mt-0.5">
                                                <CheckCircle size={12} /> Uploaded &amp; Ready
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                        <button
                                            type="button"
                                            onClick={() => setIsPdfModalOpen(true)}
                                            onPointerDown={ripple.onPointerDown}
                                            className="ripple-trigger inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <Eye size={14} />
                                            Preview PDF
                                        </button>
                                        <a
                                            href={getFileUrl(presentationPath)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <ExternalLink size={14} />
                                            Open in Tab
                                        </a>
                                        <label
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer"
                                        >
                                            Replace
                                            <input
                                                type="file"
                                                accept=".pdf,application/pdf"
                                                className="hidden"
                                                onChange={handlePresentationChange}
                                                disabled={isUploadingPresentation}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleRemovePresentation}
                                            disabled={isUploadingPresentation}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                                        >
                                            <Trash2 size={14} />
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center hover:border-primary/50 transition-colors">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                            <FileText size={24} />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-navy">
                                                Upload Demo Presentation
                                            </p>
                                            <p className="text-[11px] text-slate-400">
                                                PDF format only &bull; Max file size 25MB
                                            </p>
                                        </div>
                                        <label
                                            onPointerDown={ripple.onPointerDown}
                                            className="ripple-trigger mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-bold cursor-pointer transition-all shadow-sm"
                                        >
                                            {isUploadingPresentation ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                                                    Uploading PDF...
                                                </>
                                            ) : (
                                                <>
                                                    <Download size={14} className="rotate-180" />
                                                    Choose PDF File
                                                </>
                                            )}
                                            <input
                                                type="file"
                                                accept=".pdf,application/pdf"
                                                className="hidden"
                                                onChange={handlePresentationChange}
                                                disabled={isUploadingPresentation}
                                            />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Screenshot (Cover Page for Skill Profile) */}
                        <div className="space-y-2 pt-4 border-t border-border">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <label className="text-xs font-semibold text-navy flex items-center gap-1.5">
                                    <ImageIcon size={14} className="text-primary" />
                                    <span>Screenshot</span>
                                    <span className="text-[11px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 px-2 py-0.5 rounded-md">
                                        This will reflect on your Skill Profile
                                    </span>
                                </label>
                                <span className="text-[11px] text-slate-400">
                                    Cover page for project presentation &bull; PNG, JPG, WEBP up to 10MB
                                </span>
                            </div>

                            {screenshotPath ? (
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-16 h-11 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 bg-white shrink-0 shadow-xs">
                                            <img
                                                src={getFileUrl(screenshotPath)}
                                                alt="Project Screenshot"
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-bold text-navy truncate max-w-[200px] sm:max-w-[300px]">
                                                    {screenshotName || 'project_screenshot.png'}
                                                </span>
                                                <span className="text-[10px] uppercase font-extrabold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0">
                                                    Cover Image
                                                </span>
                                            </div>
                                            <span className="text-[11px] text-emerald-600 font-medium flex items-center gap-1 mt-0.5">
                                                <CheckCircle size={12} /> Uploaded &amp; Ready
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                                        <a
                                            href={getFileUrl(screenshotPath)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer"
                                        >
                                            <ExternalLink size={14} />
                                            Open Image
                                        </a>
                                        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer">
                                            Replace
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp,image/jpg"
                                                className="hidden"
                                                onChange={handleScreenshotChange}
                                                disabled={isUploadingScreenshot}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            onClick={handleRemoveScreenshot}
                                            disabled={isUploadingScreenshot}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50"
                                        >
                                            <Trash2 size={14} />
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center hover:border-primary/50 transition-colors">
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center">
                                            <ImageIcon size={24} />
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-navy">
                                                Upload Project Screenshot
                                            </p>
                                            <p className="text-[11px] text-slate-400">
                                                PNG, JPG, WEBP &bull; Max 10MB &bull; Displayed as the cover on your Skill Profile
                                            </p>
                                        </div>
                                        <label
                                            onPointerDown={ripple.onPointerDown}
                                            className="ripple-trigger mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-xs font-bold cursor-pointer transition-all shadow-sm"
                                        >
                                            {isUploadingScreenshot ? (
                                                <>
                                                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />
                                                    Uploading Screenshot...
                                                </>
                                            ) : (
                                                <>
                                                    <Upload size={14} />
                                                    Choose Screenshot
                                                </>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp,image/jpg"
                                                className="hidden"
                                                onChange={handleScreenshotChange}
                                                disabled={isUploadingScreenshot}
                                            />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-end gap-3">
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isSaving}
                        onPointerDown={ripple.onPointerDown}
                        style={{ '--ripple-color': 'rgba(255,255,255,0.5)' } as React.CSSProperties}
                        className="ripple-trigger px-6 py-2.5 rounded-full font-semibold shadow-soft hover:-translate-y-0.5 transition-all duration-300"
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </form>

            {/* PDF Preview Modal */}
            {presentationPath && (
                <Modal
                    isOpen={isPdfModalOpen}
                    onClose={() => setIsPdfModalOpen(false)}
                    title={presentationName || 'Demo Presentation Preview'}
                    maxWidth="max-w-[900px]"
                >
                    <div className="p-4 space-y-3">
                        <div className="flex items-center justify-between pb-2 border-b">
                            <span className="text-xs text-slate-500 font-semibold truncate max-w-[60%]">
                                {presentationName || 'Demo Presentation.pdf'}
                            </span>
                            <div className="flex items-center gap-2">
                                <a
                                    href={getFileUrl(presentationPath)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline"
                                >
                                    <ExternalLink size={13} />
                                    Open Fullscreen
                                </a>
                                <a
                                    href={getFileUrl(presentationPath)}
                                    download={presentationName || 'demo_presentation.pdf'}
                                    className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-800"
                                >
                                    <Download size={13} />
                                    Download
                                </a>
                            </div>
                        </div>
                        <div className="w-full h-[65vh] bg-slate-100 rounded-xl overflow-hidden border">
                            <iframe
                                src={`${getFileUrl(presentationPath)}#toolbar=1`}
                                title="Demo Presentation Preview"
                                className="w-full h-full border-0"
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* Change Password Component */}
            <ChangePasswordSection />
        </div>
    );
};

const ChangePasswordSection: React.FC = () => {
    const { register, handleSubmit, watch, reset, formState: { errors } } = useForm();
    const [isUpdating, setIsUpdating] = useState(false);
    const ripple = useRipple();
    const pwd = watch('new_password');

    const onPwdSubmit = async (data: any) => {
        try {
            setIsUpdating(true);
            await authService.studentChangePassword(data.new_password);
            toast.success('Password updated successfully');
            reset({ new_password: '', confirm_password: '' });
        } catch (err: any) {
            toast.error(err?.response?.data?.message || 'Failed to update password');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <form onSubmit={handleSubmit(onPwdSubmit)} className="bg-white rounded-2xl p-6 shadow-soft border border-border space-y-4">
            <h2 className="text-lg font-bold text-navy flex items-center gap-2 border-b border-border pb-3">
                <Shield className="text-primary" size={18} />
                Change Password
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                    label="New Password"
                    type="password"
                    placeholder="Enter new password"
                    registration={register('new_password', {
                        required: 'New password is required',
                        minLength: { value: 6, message: 'Password must be at least 6 characters' }
                    })}
                    error={errors.new_password?.message as string}
                />
                <Input
                    label="Confirm New Password"
                    type="password"
                    placeholder="Confirm new password"
                    registration={register('confirm_password', {
                        required: 'Please confirm your new password',
                        validate: (val) => val === pwd || 'Passwords do not match'
                    })}
                    error={errors.confirm_password?.message as string}
                />
            </div>
            <div className="flex justify-end pt-2">
                <Button
                    type="submit"
                    variant="outline"
                    disabled={isUpdating}
                    onPointerDown={ripple.onPointerDown}
                    className="ripple-trigger px-6 py-2.5 rounded-full font-semibold border-primary text-primary hover:bg-blue-bg hover:-translate-y-0.5 transition-all duration-300"
                >
                    {isUpdating ? 'Updating...' : 'Update Password'}
                </Button>
            </div>
        </form>
    );
};

export default StudentProfile;
